/**
 * WorkerPool — Resilient Supervisor for image-processor Web Workers.
 *
 * Architecture:
 * - Pool size = min(hardwareConcurrency, MAX_WORKERS) to prevent OOM on high-core machines.
 * - Supervisor Pattern (Auto-Healing): monitors worker errors and crashes (OOM, WebAssembly
 *   memory panics). When a worker dies, it rejects the stalled job, terminates the broken
 *   worker, respawns a fresh one, and keeps processing the queue without deadlocks.
 * - Timeout Safeguard: prevents runaway jobs from hanging a slot indefinitely.
 * - Zero-copy Transfer: transfers input ArrayBuffers directly to workers via Comlink.transfer.
 */

import * as Comlink from 'comlink'
import type { ImageProcessJob, ImageProcessResult } from './image-processor.worker'

const MAX_WORKERS = 4
const JOB_TIMEOUT_MS = 120_000 // 2 minutes maximum per image processing job

interface WorkerSlot {
  id: number
  worker: Worker
  proxy: Comlink.Remote<{ processImage: (job: ImageProcessJob) => Promise<ImageProcessResult> }>
  busy: boolean
  activeJob?: QueuedJob
  timeoutTimer?: ReturnType<typeof setTimeout>
}

interface QueuedJob {
  job: ImageProcessJob
  resolve: (result: ImageProcessResult) => void
  reject: (error: Error) => void
}

export class ImageWorkerPool {
  private slots: WorkerSlot[] = []
  private queue: QueuedJob[] = []
  private initialized = false

  init(): void {
    if (this.initialized) return
    const concurrency = Math.min(navigator.hardwareConcurrency || 2, MAX_WORKERS)
    for (let i = 0; i < concurrency; i++) {
      this.slots.push(this.createSlot(i))
    }
    this.initialized = true
  }

  private createSlot(id: number): WorkerSlot {
    const worker = new Worker(new URL('./image-processor.worker.ts', import.meta.url), {
      type: 'module',
    })
    const proxy = Comlink.wrap<{ processImage: (job: ImageProcessJob) => Promise<ImageProcessResult> }>(worker)
    const slot: WorkerSlot = { id, worker, proxy, busy: false }
    this.attachSlotListeners(slot)
    return slot
  }

  private attachSlotListeners(slot: WorkerSlot): void {
    slot.worker.onerror = (e) => {
      this.handleSlotCrash(
        slot,
        new Error(`Worker ${slot.id} error: ${e.message || 'Worker terminated abnormally (possible OOM)'}`),
      )
    }
    slot.worker.onmessageerror = () => {
      this.handleSlotCrash(
        slot,
        new Error(`Worker ${slot.id} message deserialization error`),
      )
    }
  }

  private handleSlotCrash(slot: WorkerSlot, error: Error): void {
    if (slot.timeoutTimer) {
      clearTimeout(slot.timeoutTimer)
      slot.timeoutTimer = undefined
    }

    // 1. Notify caller about the failure so UI doesn't hang
    if (slot.activeJob) {
      slot.activeJob.reject(error)
      slot.activeJob = undefined
    }

    // 2. Clean up dead worker
    try {
      slot.proxy[Comlink.releaseProxy]()
      slot.worker.terminate()
    } catch {
      // Ignore termination errors on dead workers
    }

    // 3. Auto-heal: spawn a fresh replacement worker in this slot
    const fresh = this.createSlot(slot.id)
    slot.worker = fresh.worker
    slot.proxy = fresh.proxy
    slot.busy = false

    // 4. Resume queue processing
    this.dispatch()
  }

  /**
   * Submits a job to the pool. Resolves when completed, rejects on error or timeout.
   */
  submit(job: ImageProcessJob): Promise<ImageProcessResult> {
    if (!this.initialized) this.init()
    return new Promise<ImageProcessResult>((resolve, reject) => {
      this.queue.push({ job, resolve, reject })
      this.dispatch()
    })
  }

  private dispatch(): void {
    const freeSlot = this.slots.find((s) => !s.busy)
    if (!freeSlot || this.queue.length === 0) return

    const queued = this.queue.shift()!
    freeSlot.busy = true
    freeSlot.activeJob = queued

    // Timeout watchdog
    freeSlot.timeoutTimer = setTimeout(() => {
      this.handleSlotCrash(
        freeSlot,
        new Error(`Worker ${freeSlot.id} timed out after ${JOB_TIMEOUT_MS / 1000}s on job ${queued.job.fileId}`),
      )
    }, JOB_TIMEOUT_MS)

    // Transfer inputBuffer zero-copy to free main thread RAM
    const transferable = [queued.job.inputBuffer]

    freeSlot.proxy
      .processImage(Comlink.transfer(queued.job, transferable))
      .then((result) => {
        if (freeSlot.timeoutTimer) {
          clearTimeout(freeSlot.timeoutTimer)
          freeSlot.timeoutTimer = undefined
        }
        freeSlot.busy = false
        freeSlot.activeJob = undefined
        queued.resolve(result)
        this.dispatch()
      })
      .catch((err: unknown) => {
        if (freeSlot.timeoutTimer) {
          clearTimeout(freeSlot.timeoutTimer)
          freeSlot.timeoutTimer = undefined
        }
        freeSlot.busy = false
        freeSlot.activeJob = undefined
        queued.reject(err instanceof Error ? err : new Error(String(err)))
        this.dispatch()
      })
  }

  get queueLength(): number {
    return this.queue.length
  }

  get activeCount(): number {
    return this.slots.filter((s) => s.busy).length
  }

  get workerCount(): number {
    return this.slots.length
  }

  terminate(): void {
    for (const slot of this.slots) {
      if (slot.timeoutTimer) {
        clearTimeout(slot.timeoutTimer)
      }
      try {
        slot.proxy[Comlink.releaseProxy]()
        slot.worker.terminate()
      } catch {
        // Ignore errors during termination
      }
    }
    this.slots = []
    this.queue = []
    this.initialized = false
  }
}

// Singleton pool for the application lifetime
export const imagePool = new ImageWorkerPool()
